import React, {ReactNode} from 'react'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import {useAuth} from 'src/app/modules/auth'

// ─── Permission Keys ─────────────────────────────────────────────────────────

export type PermissionKey =
  | 'AUDIT_VIEW'
  | 'DEPT_ADD'
  | 'DEPT_DELETE'
  | 'DEPT_EDIT'
  | 'DEPT_VIEW'
  | 'DOC_TYPE_ADD'
  | 'DOC_TYPE_DELETE'
  | 'DOC_TYPE_EDIT'
  | 'DOC_TYPE_VIEW'
  | 'LIBRARY_ACCESS_DELETE'
  | 'LIBRARY_ACCESS_EDIT'
  | 'LIBRARY_ACCESS_VIEW'
  | 'MOD_ACCESS_ADD'
  | 'MOD_ACCESS_DELETE'
  | 'MOD_ACCESS_EDIT'
  | 'MOD_ACCESS_VIEW'
  | 'POSITION_ADD'
  | 'POSITION_DELETE'
  | 'POSITION_EDIT'
  | 'POSITION_VIEW'
  | 'PROCESS_ACCESS_DELETE'
  | 'PROCESS_ACCESS_EDIT'
  | 'PROCESS_ACCESS_VIEW'
  | 'PROCESS_DESIGN_ADD'
  | 'PROCESS_DESIGN_DELETE'
  | 'PROCESS_DESIGN_EDIT'
  | 'PROCESS_DESIGN_VIEW'
  | 'REC_PROFILE_DESIGN_ADD'
  | 'REC_PROFILE_DESIGN_DELETE'
  | 'REC_PROFILE_DESIGN_EDIT'
  | 'REC_PROFILE_DESIGN_VIEW'
  | 'SEL_FIELD_ADD'
  | 'SEL_FIELD_DELETE'
  | 'SEL_FIELD_EDIT'
  | 'SEL_FIELD_VIEW'
  | 'USERS_ADD'
  | 'USERS_DELETE'
  | 'USERS_EDIT'
  | 'USERS_VIEW'

// ─── Utility Functions ────────────────────────────────────────────────────────

/**
 * Pure function: check whether a given permission key exists in a list.
 */
export function hasPermission(
  permissions: string[] | undefined,
  permission: PermissionKey
): boolean {
  if (!permissions) return false
  return permissions.includes(permission)
}

/**
 * Pure function: check whether ANY of the given permission keys exists.
 */
export function hasAnyPermission(
  permissions: string[] | undefined,
  permissionKeys: PermissionKey[]
): boolean {
  if (!permissions) return false
  return permissionKeys.some((k) => permissions.includes(k))
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * usePermissions — returns helpers bound to the current user's accessPermissions.
 *
 * Usage:
 *   const { can, canAny } = usePermissions()
 *   if (can('DEPT_ADD')) { … }
 */
export function usePermissions() {
  const {currentUser} = useAuth()
  const permissions = currentUser?.accessPermissions ?? []

  return {
    permissions,
    can: (permission: PermissionKey) => hasPermission(permissions, permission),
    canAny: (permissionKeys: PermissionKey[]) =>
      hasAnyPermission(permissions, permissionKeys),
  }
}

// ─── PermissionGuard ──────────────────────────────────────────────────────────

interface PermissionGuardProps {
  permission: PermissionKey | PermissionKey[]
  children: ReactNode
}

/**
 * PermissionGuard — renders children only when the current user has the
 * required permission(s).  Used to show/hide sidebar items, sections, etc.
 *
 * Usage:
 *   <PermissionGuard permission="DEPT_VIEW">
 *     <SidebarMenuItem … />
 *   </PermissionGuard>
 *
 *   // OR multiple (any-match):
 *   <PermissionGuard permission={['DEPT_VIEW', 'POSITION_VIEW']}>
 *     …
 *   </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
}) => {
  const {can, canAny} = usePermissions()
  const allowed = Array.isArray(permission) ? canAny(permission) : can(permission)
  if (!allowed) return null
  return React.createElement(React.Fragment, null, children)
}

// ─── PermissionButton ─────────────────────────────────────────────────────────

interface PermissionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  permission: PermissionKey
  tooltipText?: string
  children: ReactNode
}

/**
 * PermissionButton — renders a button that is disabled + gray when the user
 * lacks the required permission.  A Bootstrap tooltip is displayed on hover.
 *
 * Usage:
 *   <PermissionButton permission="DEPT_ADD" onClick={handleAdd} className="btn btn-primary btn-sm">
 *     + Add New
 *   </PermissionButton>
 */
export const PermissionButton: React.FC<PermissionButtonProps> = ({
  permission,
  tooltipText = "You don't have permission to perform this action",
  children,
  className,
  disabled,
  style,
  ...rest
}) => {
  const {can} = usePermissions()
  const allowed = can(permission)

  if (allowed) {
    return React.createElement(
      'button',
      {className, disabled, style, ...rest},
      children
    )
  }

  // Disabled state: gray button + tooltip
  const disabledStyle: React.CSSProperties = {
    ...style,
    opacity: 0.6,
    cursor: 'not-allowed',
    filter: 'grayscale(100%)',
  }

  const disabledButton = React.createElement(
    'span',
    {className: 'd-inline-block', tabIndex: 0},
    React.createElement(
      'button',
      {
        className,
        disabled: true,
        style: disabledStyle,
        ...rest,
        onClick: undefined,
      },
      children
    )
  )

  const tooltip = React.createElement(
    Tooltip,
    {id: `perm-tooltip-${permission}`},
    tooltipText
  )

  // Cast to any to avoid React Bootstrap / TS overload mismatch with createElement
  const OT = OverlayTrigger as any
  return React.createElement(
    OT,
    {placement: 'top', overlay: tooltip},
    disabledButton
  )
}
